import React, { RefObject, useEffect, useRef, useState } from 'react';

import { ReactComponent as SearchIcon } from 'assets/icons/ic_search.svg';
import MobileSearchBottomSheet from 'components/search/MobileSearchBottomSheet';
import MoveInModal from 'components/search/MoveInModal';
import WhereModal from 'components/search/WhereModal';
import { useGetWhereSearch } from 'entities/home';
import { useWhereSearchStore } from 'features/home';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { formatLocalizedDate, formatLocalizedPeriod } from 'shared/lib';
import { twMerge } from 'tailwind-merge';

import ContractPeriodModal from './ContractPeriodModal';
import SearchWrapper from './SearchWrapper';

export type SearchParams = {
  originalWhere?: string;
  whereType?: string;
  whereValue?: string;
  moveIn?: string;
  contractPeriod?: string;
};

type SearchType = 'WHERE' | 'MOVEIN' | 'PERIOD' | null;

type HomeSearchBoxProps = {
  onSearch: (params: SearchParams) => void;
};

const DEFAULT_CONTRACT_PERIOD = '6';

const HomeSearchBox = ({ onSearch }: HomeSearchBoxProps) => {
  const { t, i18n } = useTranslation('search');
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const [active, setActive] = useState<SearchType>(null);

  const [originalWhere, setOriginalWhere] = useState('');
  const [whereType, setWhereType] = useState('');
  const [whereValue, setWhereValue] = useState('');
  const [moveIn, setMoveIn] = useState('');
  const [contractPeriod, setContractPeriod] = useState(DEFAULT_CONTRACT_PERIOD);
  const [displayPeriod, setDisplayPeriod] = useState('');

  const { addWhereSearch, whereSearches } = useWhereSearchStore();

  const { data: whereData } = useGetWhereSearch(originalWhere);

  const handleChangePeriod = (value: number) => {
    setContractPeriod(value.toString());
    setDisplayPeriod(formatLocalizedPeriod(value, t('months')));
  };

  useEffect(() => {
    if (searchParams.has('originalWhere')) {
      setOriginalWhere(searchParams.get('originalWhere') as string);
    }
    if (searchParams.has('whereType')) {
      setWhereType(searchParams.get('whereType') as string);
    }
    if (searchParams.has('whereValue')) {
      setWhereValue(searchParams.get('whereValue') as string);
    }
    if (searchParams.has('moveIn')) {
      setMoveIn(searchParams.get('moveIn') as string);
    }
    if (searchParams.has('contractPeriod')) {
      setContractPeriod(searchParams.get('contractPeriod') as string);
      setDisplayPeriod(`${searchParams.get('contractPeriod')} ${t('months')}`);
    }
  }, [searchParams, t]);

  useEffect(() => {
    if (active === null) {
      if ((whereType === '' || whereValue === '') && originalWhere !== '') {
        setOriginalWhere('');
      }
    }
  }, [active, whereType, whereValue, originalWhere]);

  useEffect(() => {
    if (contractPeriod && !displayPeriod) {
      setDisplayPeriod(formatLocalizedPeriod(Number(contractPeriod), t('months')));
    }
  }, [contractPeriod, displayPeriod, t]);

  const handleSearch = () => {
    window.dataLayer?.push({
      event: 'search',
      where: originalWhere === '' ? undefined : originalWhere,
      move_in: moveIn === '' ? undefined : moveIn,
      contract_period: contractPeriod === '' ? undefined : contractPeriod,
      search_term: originalWhere === '' ? undefined : originalWhere,
      type: 'main page',
    });

    onSearch({
      originalWhere: originalWhere || undefined,
      whereType: whereType || undefined,
      whereValue: whereValue || undefined,
      moveIn: moveIn || undefined,
      contractPeriod: contractPeriod || undefined,
    });

    if (whereType && whereValue && originalWhere) {
      addWhereSearch({ id: Date.now(), whereType, whereValue, originalValue: originalWhere });
    }

    setActive(null);
  };

  return (
    <>
      {/* ===== MOBILE ===== */}
      <MobileSearchBottomSheet
        onSearch={onSearch}
        defaultContractPeriod={DEFAULT_CONTRACT_PERIOD}
        searchSource="main page"
      />

      {/* ===== DESKTOP ===== */}
      <div
        ref={searchBoxRef}
        className={twMerge(
          'relative z-10 mx-auto hidden h-[108px] w-fit flex-row items-center justify-center gap-x-[35px] rounded-[24px] bg-white px-10 py-6.25 shadow-search md:flex',
          active && 'z-20',
        )}
      >
        <div className="flex h-full items-center gap-x-[50px]">
          <SearchWrapper
            width="w-[111px]"
            subWidth="w-[810px]"
            title={t('where')}
            onDeleteAll={() => {
              setOriginalWhere('');
              setWhereType('');
              setWhereValue('');
            }}
            isActive={active === 'WHERE'}
            setActive={() => setActive('WHERE')}
            onClose={() => {
              if (whereType === '' || whereValue === '') {
                setOriginalWhere('');
              }
              setActive(null);
            }}
            value={originalWhere}
            onChange={(e) => {
              setOriginalWhere(e.target.value);
              setWhereValue('');
              setWhereType('');
            }}
            placeholder={t('searchPlaces')}
            closePosition="right-10 top-[30px]"
            position="-left-[23px]"
            ignoreRefs={[searchBoxRef as RefObject<HTMLDivElement>]}
            contentMaxHeightClassName="max-h-[420px] where-modal-scroll"
            modalContent={
              <WhereModal
                whereSearches={whereSearches}
                whereData={whereData}
                originalValue={originalWhere}
                setOriginalValue={setOriginalWhere}
                setWhereValue={setWhereValue}
                setWhereType={setWhereType}
                onClose={() => setActive(null)}
              />
            }
          />

          <ContractPeriodModal
            setActive={(value) => setActive(value as SearchType)}
            isActive={active === 'PERIOD'}
            currentPeriod={Number(contractPeriod) === 0 ? 1 : Number(contractPeriod)}
            onClose={(month: number) => {
              setContractPeriod(month.toString());
              setDisplayPeriod(formatLocalizedPeriod(month, t('months')));
              setActive(null);
            }}
            handleChangePeriod={handleChangePeriod}
          >
            <div className="flex w-[140px] flex-col gap-y-2">
              <span className="text-c2">{t('contractPeriod')}</span>
              <button
                type="button"
                className={`border-none bg-transparent text-left text-c2 ${displayPeriod ? 'text-c2' : 'text-gray-40'} outline-none`}
              >
                {displayPeriod ? displayPeriod : t('enterNumbers')}
              </button>
            </div>
          </ContractPeriodModal>
        </div>

        <SearchWrapper
          width="w-[110px]"
          subWidth="w-[391px]"
          title={t('moveIn')}
          onDeleteAll={() => {
            setMoveIn('');
          }}
          isActive={active === 'MOVEIN'}
          setActive={() => setActive('MOVEIN')}
          onClose={() => setActive(null)}
          value={moveIn ? formatLocalizedDate(moveIn, i18n.language) : moveIn}
          readOnly
          placeholder={t('addDate')}
          closePosition="right-5 top-5"
          ignoreRefs={[searchBoxRef as RefObject<HTMLDivElement>]}
          modalContent={<MoveInModal value={moveIn} setValue={setMoveIn} onClose={() => setActive(null)} />}
        />

        <button
          className="flex items-center justify-center rounded-full bg-primary p-3 text-white"
          onClick={handleSearch}
        >
          <SearchIcon width={31} height={31} />
        </button>
      </div>
    </>
  );
};

export default HomeSearchBox;
